import { supabase } from '../lib/supabase';
import { normalizeImageForUpload, assertSupportedUploadFormat } from './heicConvert';

// Brand-level images (logos + decorative elements) live in their own bucket
// and table so they are reusable across every project of the brand. The
// shape mirrors `projectAssets.ts` but scoped by `brand_profile_id` instead
// of `project_id` and only handles images.
//
// DB schema (see migration in chat history):
//   table  : public.brand_assets
//   columns: id, brand_profile_id, id_company, tipo, name, storage_path,
//            mime_type, size_bytes, width, height, sort_order, created_at
//   bucket : 'brand-assets'  (private)
//   path   : `${id_company}/${brand_profile_id}/${tipo}/${assetId}.${ext}`
// 'upload' = imágenes que el usuario sube en el editor "Edición gráfica" para
// reusar entre imágenes (mismo bucket/tabla, scoped por marca; se trata como
// imagen normal, no como fuente).
export type BrandAssetTipo = 'logo' | 'element' | 'font' | 'upload';

// Font files we accept for brand-typography uploads. Modern web-friendly
// formats only — TTF/OTF still work but woff2 is preferred for size.
const FONT_MIME_TYPES = new Set([
  'font/woff2', 'font/woff', 'font/ttf', 'font/otf',
  'application/font-woff2', 'application/font-woff',
  'application/x-font-ttf', 'application/x-font-otf',
  'application/octet-stream', // some browsers report this for ttf/otf
]);
const FONT_EXTS = new Set(['woff2', 'woff', 'ttf', 'otf']);

export interface BrandAsset {
  id: string;
  brand_profile_id: string;
  id_company: string;
  tipo: BrandAssetTipo;
  name: string | null;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  sort_order: number;
  created_at: string;
  /** Carpeta de Elementos de marca (ADR-2026-10-19-elementos-de-marca). `null` = sin
   *  carpeta: los `upload` de Edición gráfica, o un archivo anterior a las carpetas. */
  carpeta_id?: string | null;
}

export const BRAND_ASSETS_BUCKET = 'brand-assets';
const BUCKET = BRAND_ASSETS_BUCKET;

/**
 * La carpeta de Elementos de marca donde cae un archivo que se sube SIN elegir carpeta
 * (el Estudio de Video sube así): una por tipo, con el mismo nombre y color que usa la
 * migración `20261019180000_elementos_de_marca.sql` para los que ya había. Los `upload`
 * de Edición gráfica no son elementos de la marca y no van a ninguna.
 */
export const CARPETA_POR_TIPO: Partial<Record<BrandAssetTipo, { nombre: string; color: string }>> = {
  logo: { nombre: 'Logos', color: 'azul' },
  element: { nombre: 'Elementos gráficos', color: 'lavanda' },
  font: { nombre: 'Tipografías', color: 'arena' },
};

/**
 * Busca (o crea) la carpeta de su tipo para un archivo subido sin carpeta. Nunca tira:
 * si la tabla de carpetas no está o la RLS no deja, devuelve `null` y el archivo se
 * sube sin carpeta, como antes de que existieran.
 */
export async function carpetaPorDefecto(
  { companyId, brandProfileId, tipo }: { companyId: string; brandProfileId: string; tipo: BrandAssetTipo },
): Promise<string | null> {
  const destino = CARPETA_POR_TIPO[tipo];
  if (!destino) return null;
  try {
    const { data: existente, error } = await supabase
      .from('brand_asset_carpetas')
      .select('id')
      .eq('brand_id', brandProfileId)
      .eq('nombre', destino.nombre)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) return null;
    if (existente?.id) return existente.id as string;
    const { data: creada, error: errCrear } = await supabase
      .from('brand_asset_carpetas')
      .insert({ id_company: companyId, brand_id: brandProfileId, nombre: destino.nombre, color: destino.color })
      .select('id')
      .single();
    if (errCrear || !creada) return null;
    return creada.id as string;
  } catch {
    return null;
  }
}

// Same caching strategy as projectAssets: keep signed URLs in memory for
// 50min so re-renders don't hammer the Storage API. Supabase signs them
// for 1h, the 10min margin keeps us safe from clock skew.
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const SIGNED_URL_TTL_SECONDS = 3600;
const SIGNED_URL_CACHE_MS = 50 * 60 * 1000;

function extForFile(file: File): string {
  const m = file.name.match(/\.([a-zA-Z0-9]+)$/);
  if (m) return m[1].toLowerCase();
  const sub = file.type.split('/')[1];
  return sub || 'bin';
}

async function probeImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export interface UploadBrandAssetParams {
  file: File;
  brandProfileId: string;
  companyId: string;
  tipo: BrandAssetTipo;
  /**
   * La carpeta de Elementos de marca. Sin esto (`undefined`, lo que hace el Estudio),
   * la de su tipo (`carpetaPorDefecto`); con `null`, ninguna.
   */
  carpetaId?: string | null;
}

/**
 * Upload a brand asset to Storage and insert a row in `brand_assets`.
 * Handles two flavors:
 *   - images (logo / element): HEIC/AVIF normalization + dimensions probe.
 *   - font files (woff2/woff/ttf/otf): uploaded as-is, no probing.
 * The `tipo` field decides which path is taken.
 */
export async function uploadBrandAsset(
  { file, brandProfileId, companyId, tipo, carpetaId }: UploadBrandAssetParams,
): Promise<BrandAsset> {
  const isFont = tipo === 'font';
  if (isFont) {
    const ext = extForFile(file);
    const looksLikeFont = FONT_MIME_TYPES.has(file.type) || FONT_EXTS.has(ext);
    if (!looksLikeFont) {
      throw new Error(`Solo se aceptan archivos de fuente (woff2, woff, ttf, otf). Recibido: ${file.type || ext || 'desconocido'}`);
    }
  } else {
    // Rechazar formatos bloqueados (RAW/TIFF) con nuestro mensaje antes del
    // chequeo genérico de `image/*` (un .cr2 llega con type vacío y caería en
    // el mensaje "Only image files..." sin esto).
    assertSupportedUploadFormat(file);
    if (!file.type.startsWith('image/')) {
      throw new Error(`Only image files are accepted as brand assets (got ${file.type || 'unknown'})`);
    }
  }

  if (!isFont) file = await normalizeImageForUpload(file);
  const ext = extForFile(file);
  const assetId = crypto.randomUUID();
  const storagePath = `${companyId}/${brandProfileId}/${tipo}/${assetId}.${ext}`;

  // Probe image dimensions in parallel with the upload — fonts skip this.
  const probePromise = isFont ? Promise.resolve(null) : probeImageDimensions(file);

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type || undefined, upsert: false });
  if (uploadErr) {
    throw new Error(`Storage upload failed: ${uploadErr.message}`);
  }

  const probed = await probePromise;
  const carpeta =
    carpetaId !== undefined ? carpetaId : await carpetaPorDefecto({ companyId, brandProfileId, tipo });

  const row: Omit<BrandAsset, 'created_at'> = {
    id: assetId,
    brand_profile_id: brandProfileId,
    id_company: companyId,
    tipo,
    name: file.name,
    storage_path: storagePath,
    mime_type: file.type || null,
    size_bytes: file.size,
    width: probed?.width ?? null,
    height: probed?.height ?? null,
    sort_order: 0,
    // Sólo si hay carpeta: sin la columna (la migración todavía no corrió) el insert
    // de siempre sigue funcionando.
    ...(carpeta ? { carpeta_id: carpeta } : {}),
  };

  const { data, error: insertErr } = await supabase
    .from('brand_assets')
    .insert(row)
    .select('*')
    .single();

  if (insertErr || !data) {
    // Best-effort cleanup if the row insert fails after the upload
    // succeeded — otherwise the file would dangle in storage.
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => {});
    throw new Error(`Brand asset insert failed: ${insertErr?.message || 'unknown'}`);
  }

  return data as BrandAsset;
}

/**
 * Cached signed URL helper for brand assets. Mirrors `getAssetSignedUrl`
 * from projectAssets but targets the brand bucket.
 */
export async function getBrandAssetSignedUrl(storagePath: string): Promise<string | null> {
  const cached = signedUrlCache.get(storagePath);
  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.url;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;

  signedUrlCache.set(storagePath, { url: data.signedUrl, expiresAt: now + SIGNED_URL_CACHE_MS });
  return data.signedUrl;
}

/**
 * Fetch all brand assets for a given brand profile, optionally filtered by
 * `tipo`. Returns rows ordered by `sort_order` ASC then newest first so
 * future drag-to-reorder support keeps a stable visual order.
 */
export async function fetchBrandAssets(
  brandProfileId: string,
  tipo?: BrandAssetTipo,
): Promise<BrandAsset[]> {
  let q = supabase
    .from('brand_assets')
    .select('*')
    .eq('brand_profile_id', brandProfileId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (tipo) q = q.eq('tipo', tipo);
  const { data, error } = await q;
  if (error || !data) return [];
  return data as BrandAsset[];
}

/**
 * Delete a brand asset (storage object + DB row). Best-effort: if storage
 * deletion fails we still drop the row so the UI doesn't show a ghost.
 */
export async function deleteBrandAsset(asset: Pick<BrandAsset, 'id' | 'storage_path'>): Promise<void> {
  await supabase.storage.from(BUCKET).remove([asset.storage_path]).catch(() => {});
  await supabase.from('brand_assets').delete().eq('id', asset.id);
  signedUrlCache.delete(asset.storage_path);
}
